import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Endorsement, CreateEndorsementDTO, UpdateEndorsementDTO } from '@promo/shared';
import { get, post, put, deleteFn } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

/**
 * Custom hook for managing endorsements data operations
 */
export function useEndorsements(packetId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const cacheKey = ['endorsements', packetId];

  // Query: fetch endorsements for a packet
  const endorsementsQuery = useQuery<Endorsement[], Error>({
    queryKey: cacheKey,
    queryFn: () => {
      if (!packetId) return Promise.resolve([]);
      return get<Endorsement[]>('/endorsements', { packetId });
    },
    enabled: !!packetId,
  });

  // Mutation: create endorsement
  const createMutation = useMutation<Endorsement, Error, CreateEndorsementDTO>({
    mutationFn: (data) => post<Endorsement>('/endorsements', data),
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to create endorsement: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  const createEndorsement = (
    data: CreateEndorsementDTO,
    options?: { onSuccess?: () => void }
  ) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: cacheKey });
        toast({
          title: 'Success',
          description: 'Endorsement created successfully',
        });
        options?.onSuccess?.();
      },
    });
  };

  // Mutation: update endorsement
  const updateMutation = useMutation<
    Endorsement,
    Error,
    { id: string; data: UpdateEndorsementDTO }
  >({
    mutationFn: ({ id, data }) => put<Endorsement>(`/endorsements/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      toast({
        title: 'Success',
        description: 'Endorsement updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update endorsement: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Mutation: delete endorsement
  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteFn<void>(`/endorsements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      toast({
        title: 'Success',
        description: 'Endorsement deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to delete endorsement: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  return {
    endorsements: endorsementsQuery.data || [],
    isLoadingEndorsements: endorsementsQuery.isLoading,
    endorsementsError: endorsementsQuery.error,

    createEndorsement,
    isCreatingEndorsement: createMutation.isPending,

    updateEndorsement: (id: string, data: UpdateEndorsementDTO) =>
      updateMutation.mutate({ id, data }),
    isUpdatingEndorsement: updateMutation.isPending,

    deleteEndorsement: (id: string) => deleteMutation.mutate(id),
    isDeletingEndorsement: deleteMutation.isPending,

    refetchEndorsements: endorsementsQuery.refetch,
    isMutating:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
}